import {
  AbilityBuilder,
  MongoAbility,
  createMongoAbility,
} from '@casl/ability';
import { useContext, useMemo } from 'react';
import {
  AuthorizationContext,
  AuthorizationProviderProps,
} from '../../api/authorization.provider';
import {
  MachineConnectionType,
  useGetMachineQuery,
} from '../../api/graphql/generated';
import { AppMode, useAppModeContext } from '../app/app-mode-provider';

export interface MachineAuthorizationProviderProps
  extends AuthorizationProviderProps {
  teamId: string;
  machineId: string;
}

const MachineAuthorizationProvider = (
  props: MachineAuthorizationProviderProps
) => {
  const { children, machineId, teamId } = props;

  const { data } = useGetMachineQuery({
    variables: { input: { id: machineId, teamId } },
    skip: machineId === 'new',
    fetchPolicy: 'cache-first',
  });

  const machine = data?.getMachine;

  const parentAuthorizationContextValue = useContext(AuthorizationContext);
  const { mode: appMode } = useAppModeContext();

  const contextValue = useMemo<MongoAbility>(() => {
    let ability = createMongoAbility(parentAuthorizationContextValue.rules);

    /**
     * Users may access a direct-connect machine two ways:
     *  1. Directly via the machine's WiFi (read/write)
     *  2. Indirectly via the Beacon Server API (read-only)
     *
     * Since the source-of-truth for some entities differs
     * in the direct-connect vs cloud-connect connection types,
     * then depending on how the user is accessing the machine
     * then their CRUD permissions must change.
     */
    if (machine?.connectionType === MachineConnectionType.DIRECT) {
      let readonlySubjects: string[] = [];

      if (appMode === AppMode.DIRECT) {
        readonlySubjects = ['machine', 'scratchpad_attachment'];
      }

      const builder = new AbilityBuilder(createMongoAbility);

      if (readonlySubjects.length > 0) {
        builder.cannot('create', readonlySubjects);
        builder.cannot('update', readonlySubjects);
        builder.cannot('delete', readonlySubjects);
      }

      ability = createMongoAbility([
        ...parentAuthorizationContextValue.rules,
        ...builder.rules,
      ]);
    }

    return ability;
  }, [machine, parentAuthorizationContextValue, appMode]);

  return (
    <AuthorizationContext.Provider value={contextValue}>
      {children}
    </AuthorizationContext.Provider>
  );
};

export { MachineAuthorizationProvider };
